import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Compass, BarChart3, Soup, Brain, Heart, Bookmark, Zap } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1514326640560-7d063ef2aed5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwZm9vZCUyMHBsYXRlJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3ODEwMjQ2NDl8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Sparkles, title: "Mother Drive Engine", body: "A recommendation core that weighs mood, weather, comfort and your taste DNA — and tells you exactly why a dish was picked." },
  { icon: Compass, title: "Food Universe", body: "A curated atlas of 140+ dishes across continents — filterable by cuisine, diet, spice, prep time and meal." },
  { icon: Soup, title: "Taste DNA", body: "Every like and rejection refines a personal flavor profile that quietly shapes future picks." },
  { icon: BarChart3, title: "Analytics Layer", body: "Track mood trends, cuisine bias, and the dishes the world is craving right now." },
];

const TRUST_POINTS = [
  { icon: Brain,    label: "Learns your Taste DNA" },
  { icon: Heart,    label: "Understands your Mood" },
  { icon: Bookmark, label: "Remembers your Food Preferences" },
  { icon: Zap,      label: "Helps You Decide Faster" },
];

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO — editorial asymmetric */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-14 lg:pt-20 pb-16 sm:pb-20 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-6 sm:mb-7" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="overline">Food Intelligence Operating System</span>
            </div>
            <h1 className="font-serif text-[2.75rem] leading-[1.02] sm:text-5xl sm:leading-[0.98] md:text-6xl lg:text-7xl xl:text-[5.25rem] font-semibold tracking-tighter break-words [text-wrap:balance]" data-testid="hero-headline">
              Your Personal
              <br />
              Food Intelligence
              <br />
              <span className="italic text-primary">Buddy.</span>
            </h1>
            <p className="mt-6 sm:mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="hero-subtext">
              YUMO learns your tastes, mood, habits, culture, family preferences, and food memories — to help you decide what to cook, eat, order, or explore.
            </p>

            {/* Trust statement */}
            <ul className="mt-6 sm:mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-xl" data-testid="hero-trust">
              {TRUST_POINTS.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5 text-sm">
                  <Icon className="w-4 h-4 text-primary shrink-0" strokeWidth={1.75} />
                  <span className="text-foreground/90">{label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 sm:mt-9 flex flex-wrap items-center gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-full h-12 px-6" data-testid="hero-start-btn">
                  Start My Food Journey <ArrowRight className="ml-1 w-4 h-4" strokeWidth={1.75} />
                </Button>
              </Link>
              <a href="#how-yumo-works">
                <Button size="lg" variant="ghost" className="rounded-full h-12 px-6" data-testid="hero-how-btn">
                  See How YUMO Works
                </Button>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] max-h-[560px] mx-auto rounded-3xl overflow-hidden border border-border">
              <img src={HERO_IMG} alt="Curated gourmet plate" className="w-full h-full object-cover" loading="eager" decoding="async" />
            </div>
            <div className="absolute -bottom-5 -left-3 lg:-bottom-6 lg:-left-6 hidden md:block">
              <div className="bg-card border border-border rounded-2xl p-4 lg:p-5 shadow-sm max-w-[260px] lg:max-w-xs">
                <div className="overline mb-2 text-primary">Mother Drive</div>
                <div className="text-sm leading-snug">
                  &ldquo;Rainy + sad mood + medium spice → <span className="font-semibold">Ramen</span>, comfort 95.&rdquo;
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND STORY — Yummy Food For Your Mood */}
      <section className="border-t border-border" data-testid="brand-story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="overline mb-5 text-primary">Our Soul</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                Yummy Food
                <br />
                For Your
                <br />
                <span className="italic text-primary">Mood.</span>
              </h2>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/signup">
                  <Button size="lg" className="rounded-full h-12 px-6" data-testid="brand-start-btn">
                    Start My Food Journey <ArrowRight className="ml-1 w-4 h-4" strokeWidth={1.75} />
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="lg" variant="outline" className="rounded-full h-12 px-6" data-testid="brand-dna-btn">
                    Discover My Taste DNA
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5 text-base md:text-lg leading-relaxed text-foreground/85" data-testid="brand-story-copy">
              <p className="font-serif text-xl md:text-2xl text-foreground italic leading-snug">
                One amazing meal can change how you feel.
              </p>
              <ul className="space-y-3 border-l border-border pl-6">
                <li>A <span className="font-medium text-foreground">comforting breakfast</span> can energize your morning.</li>
                <li>A <span className="font-medium text-foreground">warm lunch</span> can reset a stressful day.</li>
                <li>A <span className="font-medium text-foreground">favorite dinner</span> can bring families together.</li>
                <li>A <span className="font-medium text-foreground">nostalgic dish</span> can reconnect you with home.</li>
              </ul>
              <p>YUMO exists to help people discover the food that feels right for the moment.</p>
              <p>We care about nutrition. We respect healthy eating. But our mission is larger — we help people make food decisions that improve everyday happiness.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY YUMO EXISTS */}
      <section className="border-t border-border bg-foreground text-background" data-testid="why-yumo-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="overline text-background/60 mb-5">Why YUMO Exists</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight">
            Every day, millions of people ask the same question:
            <br />
            <span className="italic text-primary">"What should I eat?"</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 text-base md:text-lg leading-relaxed text-background/85">
            <div>
              <p className="text-background font-medium mb-3">The problem isn't a lack of food.</p>
              <p>The problem is <span className="font-semibold text-background">decision fatigue</span>.</p>
              <ul className="mt-4 space-y-1.5 text-background/75">
                <li>— Too many options.</li>
                <li>— Too many menus.</li>
                <li>— Too many conflicting opinions.</li>
              </ul>
            </div>
            <div>
              <p>YUMO simplifies food decisions by understanding your <span className="text-background font-medium">mood</span>, <span className="text-background font-medium">preferences</span>, <span className="text-background font-medium">culture</span>, <span className="text-background font-medium">habits</span>, and <span className="text-background font-medium">food memories</span>.</p>
              <p className="mt-4">Instead of searching endlessly, YUMO helps you discover food that <span className="italic">feels right</span>.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="how-yumo-works" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 border-t border-border scroll-mt-20">
        <div className="overline mb-4">How YUMO Works</div>
        <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight max-w-3xl leading-tight">
          A caring, intelligent companion for every food decision — not a recipe book.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {features.map((f) => (
            <div key={f.title} className="p-6 border border-border rounded-2xl bg-card hover:-translate-y-1 transition-all" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <f.icon className="w-6 h-6 text-primary mb-4" strokeWidth={1.5} />
              <h3 className="font-serif text-xl font-semibold mb-2 tracking-tight">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20 border-t border-border text-center">
        <div className="overline mb-6">Early Tasters</div>
        <p className="font-serif text-2xl md:text-4xl font-medium leading-tight tracking-tight max-w-3xl mx-auto">
          "It's the first food app that actually feels like it knows me. The 'why' under every pick is the part I didn't know I needed."
        </p>
        <div className="mt-6 text-sm text-muted-foreground">— Anika, beta user · YUMO v0.9</div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 border-t border-border">
        <div className="bg-foreground text-background rounded-3xl p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          <div>
            <div className="overline text-background/60 mb-3">Ready?</div>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
              Stop scrolling.
              <br />Start eating well.
            </h2>
          </div>
          <Link to="/signup">
            <Button size="lg" className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90" data-testid="cta-signup-btn">
              Create my Taste DNA <ArrowRight className="ml-1 w-4 h-4" strokeWidth={1.75} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
